import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Wallet, TrendUp, CheckCircle, ArrowRight } from '@phosphor-icons/react';

export function HowInvestingWorksModal({ open, onOpenChange, product, formatCurrency }) {
  const amount = product.investment_amount || 0;
  const pct = product.investment_percentage || 0;
  const price = product.price || 0;
  const perSale = (price * pct) / 100;
  const salesToRecoup = perSale > 0 ? Math.ceil(amount / perSale) : null;

  const steps = [
    { icon: Wallet, title: 'Back this product', desc: `Invest ${formatCurrency(amount)} from your wallet to claim a ${pct}% revenue share.` },
    { icon: TrendUp, title: 'Earn on every sale', desc: `Each time it sells, ${pct}% of the sale price lands in your wallet automatically — before platform fees.` },
    { icon: CheckCircle, title: 'Recoup & complete', desc: `Dividends keep flowing until you earn back your full ${formatCurrency(amount)}. Then your investment completes.` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0D1F17] border border-[#22C55E]/25 text-white rounded-xl max-w-md p-0 overflow-hidden" data-testid="how-investing-works-modal">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
            How DiviSpend investing works
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4">
          {steps.map((s, i) => (
            <div key={s.title} className="flex items-start gap-3" data-testid={`investing-step-${i + 1}`}>
              <div className="w-9 h-9 rounded-full bg-[#22C55E]/10 border border-[#22C55E]/30 flex items-center justify-center shrink-0">
                <s.icon className="w-4.5 h-4.5 text-[#22C55E]" size={18} />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{i + 1}. {s.title}</p>
                <p className="text-xs text-[#999999] leading-relaxed mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}

          {/* Dividend math at a glance */}
          <div className="bg-[#111111] border border-[#22C55E]/15 rounded-lg p-4" data-testid="dividend-math-box">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#22C55E] font-bold mb-3">Dividend math at a glance</p>
            <div className="flex items-center justify-between gap-1 text-center">
              <div className="flex-1">
                <p className="text-sm font-bold text-white">{formatCurrency(price)}</p>
                <p className="text-[9px] text-[#666666] uppercase tracking-wider">Sale price</p>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-[#444444] shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-[#22C55E]">× {pct}%</p>
                <p className="text-[9px] text-[#666666] uppercase tracking-wider">Your share</p>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-[#444444] shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-[#22C55E]">{formatCurrency(perSale)}</p>
                <p className="text-[9px] text-[#666666] uppercase tracking-wider">Per sale</p>
              </div>
            </div>
            {salesToRecoup && (
              <p className="text-xs text-[#999999] text-center mt-3 pt-3 border-t border-[#1f1f1f]">
                ≈ <span className="text-white font-semibold">{salesToRecoup} sales</span> to recoup your {formatCurrency(amount)} investment
              </p>
            )}
          </div>

          <p className="text-[10px] text-[#666666] leading-relaxed">
            Payouts are automatic — no action needed after you invest. If sales stop, dividends pause; investments are not guaranteed returns.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
